import {
  usernameUniqueRequest,
  emailUniqueRequest
} from '~/src/views/registration/lib/validation_requests.js';

import validEmailRegExp from '~/src/features/registration/lib/valid_email_reg_exp.js';

/** @typedef {import('../../shared_ui/custom_elements/form_item/types.js').Validation} Validation */
/** @typedef {import('../../shared_ui/custom_elements/form_item/types.js').ValidationCollection} ValidationCollection */

/**
 * Restricted email characters that should not be allowed in email addresses.
 * This matches RESTRICTED_EMAIL_CHARACTERS in app/models/valid_email.rb
 */
const RESTRICTED_EMAIL_CHARACTERS = [' ', ',', ';', ':', '<', '>', '(', ')', '[', ']', '{', '}', '|', '\\', '/', '..', '.@'];

/** @type ValidationCollection */
const validations = {
  required: {
    isValid: (input) => {
      const valid = !input.validity.valueMissing;

      // If the input is a select, we need to check if the value is not '0'
      if(input.type === 'select-one') {
        return valid && input.value !== '0';
      } else {
        return valid;
      }
    },
    allowNeutral: false,
    message: () => 'This field is required.',
  },
  minlength: {
    isValid: (input) => {
      return !input.validity.tooShort && input.value !== '';
    },
    allowNeutral: true,
    message: (input) => `Must be at least ${input.getAttribute('minlength')} characters.`,
  },
  mustContainNumber: {
    isValid: (input) => {
      return !input.validity.patternMismatch && input.value !== '';
    },
    allowNeutral: true,
    pattern: ".*\\d{1}.*",
    message: () => 'Must contain a number.',
  },
  mustContainNumberAndLetter: {
    isValid: (input) => {
      return !input.validity.patternMismatch && input.value !== '';
    },
    allowNeutral: true,
    pattern: ".*([0-9].*[a-zA-Z]|[a-zA-Z].*[0-9]){1}.*",
    message: () => 'Must contain a number and a letter.',
  },
  emailUnique: {
    isValid: async (input) => {
      let valid = null;

      // Don't make ajax call if there's no value
      if (input.value !== '') {
        const result = await emailUniqueRequest(input.value);
        valid = result.status === 'success' ? true : false;
      } else {
        // If there's no value, don't flag as invalid
        valid = true;
      }
      return valid;
    },
    allowNeutral: false,
    message: (_, isValid) => {
      return isValid ? 'Available email' : 'That email is taken.';
    },
  },
  emailValid: {
    isValid: (input) => {
      input.checkValidity();

      // Check regex pattern first
      const matchesPattern = !(input.validity.patternMismatch);
      if (!matchesPattern) {
        return false;
      }

      // Check for restricted characters (matches server-side validation in app/models/valid_email.rb)
      const emailValue = input.value;
      if (!emailValue) {
        return true; // Empty values are handled by required validation
      }

      const hasRestrictedCharacters = RESTRICTED_EMAIL_CHARACTERS.some(
        (char) => emailValue.includes(char)
      );

      return !hasRestrictedCharacters;
    },
    allowNeutral: false,
    pattern: validEmailRegExp.source,
    message: () => 'Please enter a valid email address.',
  },
  usernameUnique: {
    isValid: async (input) => {
      let valid = null;

      // Don't make ajax call if there's no value
      if (input.value !== '') {
        const result = await usernameUniqueRequest(input.value);
        valid = result.status === 'success' ? true : false;
      } else {
        // If there's no value, don't flag as invalid
        valid = true;
      }
      return valid;
    },
    allowNeutral: false,
    message: (_, isValid) => {
      return isValid ? 'Available username.' : 'That username is taken.'
    },
  },
  passwordsMustMatch: {
    isValid: (input) => {
      const valid = document.querySelector('#user_password').value === input.value;
      return valid;
    },
    allowNeutral: false,
    message: () => 'Passwords must match.'
  },
  schoolName: {
    isValid: (input) => {
      return !input.validity.valueMissing
    },
    allowNeutral: true,
    message: () => 'Please enter a valid institution name.',
  },
  schoolCity: {
    isValid: (input) => {
      return !input.validity.valueMissing
    },
    allowNeutral: true,
    message: () => 'Please enter a valid city.',
  },
  schoolState: {
    isValid: (input) => {
      return !input.validity.valueMissing
    },
    allowNeutral: true,
    message: () => 'Please enter a valid State or Province name.',
  },
  schoolCountry: {
    isValid: (input) => {
      return !input.validity.valueMissing
    },
    allowNeutral: true,
    message: () => 'Please enter a valid country.',
  },
}

const rulesList = Object.keys(validations);

export {
  validations,
  rulesList,
}
